import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Mail, MapPin, Phone } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const lastUpdated = "March 2025";
  
  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="privacy-policy-page">
      {/* Header */}
      <section className="py-12 px-6 bg-gradient-to-b from-charcoal to-charcoal/90">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Shield className="mx-auto mb-4 text-gold" size={48} />
            <h1 className="font-serif text-4xl text-white mb-4">Privacy Policy</h1>
            <p className="text-white/70">Last updated: {lastUpdated}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-gold/10 prose prose-lg max-w-none">
            
            <h2 className="font-serif text-2xl text-charcoal mt-0">Introduction</h2>
            <p className="text-charcoal/80">
              Living Fruit Ministry ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you visit our website livingfruitministry.org and use our services.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Information We Collect</h2>
            
            <h3 className="font-serif text-xl text-charcoal">Personal Information</h3>
            <p className="text-charcoal/80">We may collect personal information that you voluntarily provide, including:</p>
            <ul className="text-charcoal/80">
              <li>Name and contact information (email, phone number, address)</li>
              <li>Prayer requests and personal testimonies you choose to share</li>
              <li>Volunteer and event registration information</li>
              <li>Donation information (processed securely through third-party payment processors)</li>
              <li>Business directory submissions</li>
              <li>Feedback and suggestions</li>
            </ul>

            <h3 className="font-serif text-xl text-charcoal">Automatically Collected Information</h3>
            <p className="text-charcoal/80">When you visit our website, we may automatically collect:</p>
            <ul className="text-charcoal/80">
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent</li>
              <li>Referring website</li>
              <li>IP address (anonymized)</li>
            </ul>

            <h2 className="font-serif text-2xl text-charcoal">How We Use Your Information</h2>
            <p className="text-charcoal/80">We use the information we collect to:</p>
            <ul className="text-charcoal/80">
              <li>Respond to prayer requests and provide spiritual support</li>
              <li>Process donations and send tax receipts</li>
              <li>Communicate about events, programs, and ministry updates</li>
              <li>Improve our website and services</li>
              <li>Coordinate volunteer activities and community resources</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="font-serif text-2xl text-charcoal">Information Sharing</h2>
            <p className="text-charcoal/80">
              We do not sell, trade, or rent your personal information to third parties. We may share 
              information with:
            </p>
            <ul className="text-charcoal/80">
              <li><strong>Service Providers:</strong> Third parties who help us operate our website and process donations</li>
              <li><strong>Partner Organizations:</strong> Such as Smallest Souls Rescue for animal rescue coordination (with your consent)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>

            <h2 className="font-serif text-2xl text-charcoal">Data Security</h2>
            <p className="text-charcoal/80">
              We implement appropriate security measures to protect your personal information, including:
            </p>
            <ul className="text-charcoal/80">
              <li>SSL/TLS encryption for data transmission</li>
              <li>Secure data storage with access controls</li>
              <li>Regular security assessments</li>
              <li>Staff training on data protection</li>
            </ul>

            <h2 className="font-serif text-2xl text-charcoal">Your Rights</h2>
            <p className="text-charcoal/80">You have the right to:</p>
            <ul className="text-charcoal/80">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your information</li>
              <li>Opt out of marketing communications</li>
              <li>Withdraw consent where applicable</li>
            </ul>

            <h2 className="font-serif text-2xl text-charcoal">Cookies</h2>
            <p className="text-charcoal/80">
              Our website uses cookies to enhance your browsing experience. These include:
            </p>
            <ul className="text-charcoal/80">
              <li><strong>Essential Cookies:</strong> Required for basic site functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
              <li><strong>Preference Cookies:</strong> Remember your language and display preferences</li>
            </ul>
            <p className="text-charcoal/80">
              You can control cookies through your browser settings.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Children's Privacy</h2>
            <p className="text-charcoal/80">
              Our website is not directed to children under 13. We do not knowingly collect personal 
              information from children. If you believe we have collected information from a child, 
              please contact us immediately.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Changes to This Policy</h2>
            <p className="text-charcoal/80">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new policy on this page and updating the "Last updated" date.
            </p>

            <h2 className="font-serif text-2xl text-charcoal">Contact Us</h2>
            <p className="text-charcoal/80">
              If you have questions about this Privacy Policy, please contact us:
            </p>
            <div className="bg-cream rounded-xl p-6 not-prose">
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-charcoal/80">
                  <Mail size={18} className="text-gold" />
                  <span>livingfruitministry@gmail.com</span>
                </div>
                <div className="flex items-center gap-3 text-charcoal/80">
                  <Phone size={18} className="text-gold" />
                  <span>(407) 300-6299</span>
                </div>
                <div className="flex items-center gap-3 text-charcoal/80">
                  <MapPin size={18} className="text-gold" />
                  <span>109 N Beaumont Avenue #1078, Kissimmee, FL 34741</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
